"use client";

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  X,
  Check,
  ChevronDown,
  Loader,
  FileCheck,
  Plus,
  Trash2,
  AlertTriangle,
  XCircle,
  Info,
  Clock,
  Zap,
  Send,
  Share,
  CircleX,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/hook/useAuth";
import useToastNotification from "@/hook/useToastNotification";
import { sendCustomGaEvent } from "@/scripts/ga";
import {
  applyForCourse,
  getAppliedCourse,
} from "@/store/feature/courses/courseApi";
import {
  getAppliedApplications,
  getUploadedDocument,
  getUserDetail,
} from "@/store/feature/user/userApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { CourseItem } from "@/store/types/courseTypes";
import DocumentUploadOverlay from "@/components/common/DocumentUploadOverlay";
import ConfirmExtractedDetails from "@/components/common/DocumentUpload/ConfirmExtractedDetails";
import { useParallelDocumentUpload } from "@/components/common/useParallelDocumentUpload";
import {
  academicRecords,
  qualificationOptions,
} from "@/components/common/Helper";
import axiosInstance from "@/utils/axiosInstance";
import { getCampusDetails } from "@/app/actions/CampusDetails";
import { CampusDetails } from "@/store/types/campusDetails";
import EmailVerification from "@/components/common/EmailVerificationPopup";

interface DocumentType {
  id: string;
  name: string;
  status: boolean;
  disabled?: boolean;
  optional?: boolean;
}

interface ConfirmationModalProps {
  isOpen: boolean;
  hasUploadedDocs?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

interface DeleteDocumentModalProps {
  isOpen: boolean;
  documentName: string;
  isDeleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const getCountryName = (countryString?: string | null) => {
  const trimmedCountry = countryString?.trim();
  if (!trimmedCountry) return "";

  const countryParts = trimmedCountry.split(/\s+-\s+/).filter(Boolean);
  return countryParts[countryParts.length - 1]?.trim() || trimmedCountry;
};

const getCourseCountryName = (course?: CourseItem) =>
  getCountryName(course?.address_info?.country || course?.country);

const formatMonthYear = (monthYear: string) => {
  const parts = monthYear.split("/");
  let month: number;
  let year: number;

  if (parts.length === 3) {
    const [, m, y] = parts;
    month = Number(m);
    year = Number(y);
  } else if (parts.length === 2) {
    const [d, m] = parts;
    month = Number(d);
    year = Number(m);
  } else {
    return monthYear;
  }

  return new Date(year, month - 1).toLocaleString("default", {
    month: "short",
    year: "numeric",
  });
};

const isNoEnglishTest = (test?: string | null) => {
  if (!test) return false;
  const normalized = test
    .toLowerCase()
    .trim()
    .replace(/['\u2019]/g, "")
    .replace(/[^a-z0-9]/g, "");
  return (
    normalized.includes("dont") ||
    normalized.includes("notest") ||
    normalized.includes("none") ||
    normalized.includes("waiver") ||
    normalized.includes("waive") ||
    normalized.includes("haveany") ||
    normalized === "no"
  );
};

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  hasUploadedDocs = false,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  return (
    <div className="ff_poppins fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-[500px] rounded-[24px] shadow-2xl overflow-hidden relative p-6 sm:p-8 text-center border border-gray-100 animate-in fade-in zoom-in-95 duration-200">
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 w-[40px] h-[40px] bg-[#E8E3FF] flex items-center justify-center rounded-full hover:bg-primary/20 transition-colors z-20 cursor-pointer"
        >
          <X className="size-5 text-primary" />
        </button>

        <div className="mx-auto w-14 h-14 bg-amber-50 rounded-2xl flex items-center justify-center mb-4 text-amber-500 shadow-inner">
          <AlertTriangle className="size-7" />
        </div>

        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          Almost There! 🚀
        </h2>

        <p className="text-sm sm:text-base text-gray-600 font-medium mb-3">
          {hasUploadedDocs
            ? "Your uploaded documents and progress will be saved."
            : "You're just a few steps away from completing your application."}
        </p>

        <p className="text-xs sm:text-sm text-gray-500 bg-gray-50 p-3.5 rounded-xl border border-gray-100 mb-6 leading-relaxed">
          {hasUploadedDocs
            ? "Your progress is saved, but your application hasn't been submitted yet. Would you like to finish now or complete it later?"
            : "If you leave now, your selections will be saved — but your application is not submitted yet. Do you want to finish now or come back later?"}
        </p>

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="w-1/2 py-3.5 px-4 rounded-xl border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors cursor-pointer"
          >
            Leave for later
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="w-1/2 py-3.5 px-4 rounded-xl bg-primary-gradient text-white font-semibold text-sm hover:opacity-95 shadow-md transition-all cursor-pointer"
          >
            Finish Now
          </button>
        </div>
      </div>
    </div>
  );
};

const DeleteDocumentModal: React.FC<DeleteDocumentModalProps> = ({
  isOpen,
  documentName,
  isDeleting,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[160] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-md p-6 ff_poppins">
        <h3 className="text-lg font-semibold mb-3">Delete Document</h3>
        <p className="text-sm text-gray-600 mb-6">
          Are you sure you want to delete{" "}
          <span className="font-medium">{documentName}</span>?
        </p>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={isDeleting}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm disabled:opacity-70"
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Step indicator ───────────────────────────────────────────────────────────

const STEPS = ["Intake & Campus", "Documents", "Submitted"];

function StepBar({ current }: { current: number }) {
  return (
    <div className="grid grid-cols-3 gap-3 mt-4 sm:mt-5 md:mt-6 lg:mt-8">
      {STEPS.map((label, i) => (
        <div key={label} className="flex flex-col gap-1.5">
          <div className="relative h-[8px] sm:h-[10px] rounded-full overflow-hidden bg-[#D9D9D9]">
            <motion.div
              className="absolute inset-0 rounded-full bg-primary"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: i <= current ? 1 : 0 }}
              transition={{ duration: 0.5, ease: "easeInOut", delay: i * 0.07 }}
              style={{ originX: 0 }}
            />
          </div>
          <p
            className={`text-[10px] sm:text-xs md:text-sm font-medium ff_poppins`}
          >
            {label}
          </p>
        </div>
      ))}
    </div>
  );
}

// ─── Slide variants ───────────────────────────────────────────────────────────

const slideVariants = {
  enter: (dir: number) => ({
    x: dir > 0 ? 60 : -60,
    opacity: 0,
  }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({
    x: dir > 0 ? -60 : 60,
    opacity: 0,
  }),
};

// ─── Step 1 — Intake & Campus ─────────────────────────────────────────────────

function Step1({
  coursesCards,
  selectedIntake,
  setSelectedIntake,
  selectedCampus,
  setSelectedCampus,
  onNext,
  campusLoading,
}: {
  coursesCards?: CourseItem;
  selectedIntake: number | null;
  setSelectedIntake: (i: number) => void;
  selectedCampus: string;
  setSelectedCampus: (c: string) => void;
  onNext: () => void;
  campusLoading?: boolean;
}) {
  const [campusOpen, setCampusOpen] = useState(false);

  const campusOptionsCount = useMemo(() => {
    if (coursesCards?.campusDetails) {
      return Object.keys(coursesCards.campusDetails).length;
    }
    if (coursesCards?.campus_name) {
      return coursesCards.campus_name.split(",").filter(Boolean).length;
    }
    return 0;
  }, [coursesCards]);

  const canProceed =
    selectedIntake !== null &&
    (campusOptionsCount === 0 || selectedCampus !== "") &&
    !campusLoading;

  const convertDate = ({
    date,
    showDate,
  }: {
    date: string;
    showDate?: boolean;
  }) => {
    const [dateString, month, year] = date.split("/").map(Number);
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return `${showDate && !isNaN(dateString) ? dateString + " " : ""}${monthNames[month - 1] || ""} ${year || ""}`.trim();
  };

  return (
    <div className="space-y-8">
      {/* SELECT CAMPUS */}
      {campusOptionsCount > 0 && (
        <div>
          <p className="text-[11px] font-bold tracking-widest text-primary ff_poppins mb-3">
            SELECT CAMPUS{" "}
            {campusLoading && (
              <Loader size={12} className="inline ml-1 animate-spin" />
            )}
          </p>
          <div className="relative">
            <button
              onClick={() => setCampusOpen((o) => !o)}
              className="w-full flex items-center justify-between px-5 py-4 rounded-2xl border-2 border-[#D8CFFF] bg-white text-left hover:border-primary transition-colors"
            >
              <span
                className={`font-medium ff_poppins text-sm ${
                  selectedCampus ? "text-[#1a1a2e]" : "text-[#ABABAB]"
                }`}
              >
                {selectedCampus ||
                  (campusLoading ? "Loading details..." : "Choose a campus…")}
              </span>
              <motion.div
                animate={{ rotate: campusOpen ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronDown className="size-4 text-[#6B2FFA]" />
              </motion.div>
            </button>
            <AnimatePresence>
              {campusOpen && (
                <motion.ul
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.18 }}
                  className="absolute z-25 mt-2 w-full bg-[#FFFFFF] border border-[#D8CFFF] rounded-2xl shadow-xl max-h-[150px] overflow-y-auto"
                >
                  {coursesCards?.campusDetails ? (
                    Object.values(coursesCards.campusDetails).map((c, i) => (
                      <li key={i}>
                        <button
                          onClick={() => {
                            setSelectedCampus(c.name);
                            setCampusOpen(false);
                          }}
                          className={`w-full text-left px-5 py-3 text-sm font-medium ff_poppins hover:bg-[#F0ECFF] transition-colors ${
                            selectedCampus === c.name
                              ? "text-primary"
                              : "text-[#1a1a2e]"
                          }`}
                        >
                          {c.name} {c.address ? `- ${c.address}` : ""}
                        </button>
                      </li>
                    ))
                  ) : coursesCards?.campus_name ? (
                    coursesCards.campus_name
                      .split(",")
                      .map((c) => c.trim())
                      .filter(Boolean)
                      .map((campusName, i) => (
                        <li key={i}>
                          <button
                            onClick={() => {
                              setSelectedCampus(campusName);
                              setCampusOpen(false);
                            }}
                            className={`w-full text-left px-5 py-3 text-sm font-medium ff_poppins hover:bg-[#F0ECFF] transition-colors ${
                              selectedCampus === campusName
                                ? "text-primary"
                                : "text-[#1a1a2e]"
                            }`}
                          >
                            {campusName}
                          </button>
                        </li>
                      ))
                  ) : null}
                </motion.ul>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* SELECT INTAKE */}
      <div>
        <p className="text-[11px] font-bold tracking-widest text-primary ff_poppins mb-3">
          SELECT INTAKE
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {coursesCards?.intake?.month.map((opt, i) => {
            const active = selectedIntake === i;
            return (
              <motion.button
                key={i}
                onClick={() => setSelectedIntake(i)}
                whileTap={{ scale: 0.98 }}
                className={`relative text-left rounded-2xl px-5 py-5 border-2 flex items-center gap-4 transition-colors duration-200 ${
                  active
                    ? "border-primary bg-primary-gradient text-white shadow-lg shadow-primary/30"
                    : "border-[#D8CFFF] bg-white text-[#1a1a2e] cursor-pointer"
                }`}
              >
                <div
                  className={`size-7 flex-shrink-0 flex items-center justify-center rounded-full transition-colors ${
                    active ? "bg-white/20" : "bg-[#EDE9FF]"
                  }`}
                >
                  {active && (
                    <Check
                      className="size-4"
                      color={active ? "#fff" : "#6B2FFA"}
                      strokeWidth={2.5}
                    />
                  )}
                </div>
                <div>
                  <p className="font-semibold text-base sm:text-lg ff_poppins">
                    {convertDate({ date: opt, showDate: false })}
                  </p>
                  <p
                    className={`text-sm sm:text-base font-medium ff_poppins mt-0.5 ${
                      active ? "text-white/70" : "text-[#9B9B9B]"
                    }`}
                  >
                    Deadline Date:{" "}
                    {convertDate({
                      date: String(coursesCards?.intake?.deadline[i]),
                      showDate: true,
                    })}
                  </p>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* CTA */}
      <motion.button
        onClick={onNext}
        disabled={!canProceed}
        whileTap={canProceed ? { scale: 0.98 } : {}}
        className={`w-full py-4 rounded-full font-semibold text-sm ff_poppins transition-all duration-200 ${
          canProceed
            ? "bg-primary text-white"
            : "bg-[#c0c0c0ab] text-white cursor-not-allowed"
        }`}
      >
        Continue to documents
      </motion.button>
    </div>
  );
}

// ─── Step 2 — Documents ───────────────────────────────────────────────────────

interface Step2Props {
  documents: DocumentType[];
  jobs: any;
  isDocumentCompleted: (doc: DocumentType) => boolean;
  getUploadedDocumentByType: (docId: string) => any;
  deletingDocType: string;
  canDeleteUploadedDocument: (docId: string) => boolean;
  removeJob: (docId: string) => void;
  setActiveTab: (docId: string) => void;
  setForceShowVerification: (show: boolean) => void;
  openUploadOverlay: (docId: string) => void;
  setDeleteModalState: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      docType: string;
      docName: string;
    }>
  >;
  englishProficiencyDetails: any;
  noEnglishTest: boolean;
  handleCheckboxChange: (checked: boolean) => void;
  agreeToTerms: boolean;
  setAgreeToTerms: (checked: boolean) => void;
  setErrorMsg: (msg: string) => void;
  errorMsg: string;
  onBack: () => void;
  onSubmit: () => void;
  submitting: boolean;
}

function Step2({
  documents,
  jobs,
  isDocumentCompleted,
  getUploadedDocumentByType,
  deletingDocType,
  canDeleteUploadedDocument,
  removeJob,
  setActiveTab,
  setForceShowVerification,
  openUploadOverlay,
  setDeleteModalState,
  englishProficiencyDetails,
  noEnglishTest,
  handleCheckboxChange,
  agreeToTerms,
  setAgreeToTerms,
  setErrorMsg,
  errorMsg,
  onBack,
  onSubmit,
  submitting,
}: Step2Props) {
  const allUploaded = documents.every(
    (d) =>
      d.optional ||
      d.status ||
      (jobs[d.id] &&
        (jobs[d.id].status === "verification_pending" ||
          jobs[d.id].status === "saving")) ||
      (jobs[d.id] && jobs[d.id].status === "completed"),
  );

  return (
    <div className="space-y-6">
      <div className="mt-3">
        <h3 className="text-sm sm:text-lg ff_poppins font-semibold mb-2">
          Upload Documents
        </h3>

        {(!englishProficiencyDetails?.test ||
          isNoEnglishTest(englishProficiencyDetails?.test)) && (
          <div className="flex items-center gap-2 mb-4">
            <Checkbox
              id="no-english-test"
              checked={noEnglishTest}
              onCheckedChange={(checked) =>
                handleCheckboxChange(checked === true)
              }
            />
            <label
              htmlFor="no-english-test"
              className="text-sm font-medium ff_poppins cursor-pointer"
            >
              I don&apos;t have any English test
            </label>
          </div>
        )}

        <p className="text-[#364261] font-medium ff_poppins text-sm mb-4">
          Accepted file types include PDF and DOCX, with a maximum file size of
          5 MB.
        </p>

        <div className="flex flex-col gap-3">
          {documents.map((doc) => {
            const job = jobs[doc.id];
            const isCompleted = isDocumentCompleted(doc);
            const isProcessing =
              job &&
              (job.status === "extracting" || job.status === "uploading");
            const isPending = job && job.status === "verification_pending";
            const isSaving = job && job.status === "saving";
            const hasError = job && job.status === "error";
            const uploadedDoc = getUploadedDocumentByType(doc.id);
            const isDeleting = deletingDocType === doc.id;
            const canDelete = canDeleteUploadedDocument(doc.id);

            return (
              <div key={doc.id}>
                <button
                  className={`w-full flex items-center justify-between border-2 gap-1 py-2 sm:py-5 px-2 sm:px-5 rounded-lg ${
                    isCompleted
                      ? "bg-[#DDEBE1] hover:bg-green-100 border-dashed border-[#2F7D5B]"
                      : "bg-white hover:bg-blue-100 border-solid border-[#D0C4FF]"
                  } ${
                    isProcessing || isSaving
                      ? "cursor-not-allowed opacity-70"
                      : "cursor-pointer"
                  }`}
                  onClick={() => {
                    if (hasError) removeJob(doc.id);
                    if (isPending || isSaving) {
                      setActiveTab(doc.id);
                      setForceShowVerification(true);
                      return;
                    }
                    if (
                      !isProcessing &&
                      !isSaving &&
                      !isCompleted &&
                      !doc.disabled
                    ) {
                      openUploadOverlay(doc.id);
                    }
                  }}
                  disabled={isProcessing || isSaving}
                >
                  <div className="flex items-center justify-center gap-2">
                    {isProcessing ? (
                      <>
                        <Loader
                          size={20}
                          className="animate-spin text-blue-600"
                        />
                        <span className="ff_poppins text-sm sm:text-md">
                          {job.status === "extracting"
                            ? "Extracting..."
                            : "Uploading..."}
                        </span>
                      </>
                    ) : isSaving ? (
                      <>
                        <Loader
                          size={20}
                          className="animate-spin text-blue-600"
                        />
                        <span className="ff_poppins text-sm sm:text-md">
                          Saving...{doc.optional ? " (Optional)" : ""}
                        </span>
                      </>
                    ) : doc.status || job?.status === "completed" ? (
                      <>
                        <span className="size-[40px] bg-[#EFE6D6] rounded-[8px] flex items-center justify-center">
                          <Share className="size-[24px] text-[#818181]" />
                        </span>
                        <span className="truncate max-w-[150px] text-sm font-semibold ff_poppins">
                          {doc.name}
                          {doc.optional ? " (Optional)" : ""}
                        </span>
                      </>
                    ) : isPending ? (
                      <>
                        <AlertTriangle size={20} className="text-yellow-600" />
                        <span className="ff_poppins text-sm sm:text-md text-yellow-700">
                          Click Here to verify Details
                          {doc.optional ? " (Optional)" : ""}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="size-[40px] bg-[#EFE6D6] rounded-[8px] flex items-center justify-center">
                          <Share className="size-[24px] text-[#818181]" />
                        </span>
                        <span className="ff_poppins text-sm font-semibold ff_poppins">
                          {doc.name}
                          {doc.optional ? " (Optional)" : ""}
                        </span>
                      </>
                    )}
                  </div>
                  {canDelete ? (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isDeleting) return;
                        setDeleteModalState({
                          isOpen: true,
                          docType: doc.id,
                          docName: uploadedDoc?.original_filename || doc.name,
                        });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          if (isDeleting) return;
                          setDeleteModalState({
                            isOpen: true,
                            docType: doc.id,
                            docName: uploadedDoc?.original_filename || doc.name,
                          });
                        }
                      }}
                      className={`mt-1 text-red-500 hover:text-red-700 transition-colors p-1 ${
                        isDeleting
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer"
                      }`}
                      title="Delete Document"
                    >
                      {isDeleting ? (
                        <Loader size={14} className="animate-spin" />
                      ) : (
                        <span className="size-[40px] flex items-center justify-center rounded-full bg-[#EFE6D6]">
                          <CircleX color="#C30000" />
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="inline-block bg-[#EFE6D6] rounded-full py-2 px-4 text-base ff_poppins font-medium">
                      PDF/ PNG
                    </span>
                  )}
                  {hasError && (
                    <span className="text-xs text-red-500 font-medium px-2">
                      {job.errorMsg || "Failed"}
                    </span>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>
      <div className="rounded-[12px] py-2 px-[12px] flex items-center gap-[10px] bg-[#DBEAFE]">
        <Info className="text-primary" />
        <p className="text-sm ff_poppins text-primary">
          Accepted formats: PDF, JPG, PNG. Max 5MB per file. All documents must
          be clearly readable.
        </p>
      </div>
      <div className="flex items-start gap-2 mt-5">
        <Checkbox
          id="terms"
          checked={agreeToTerms}
          onCheckedChange={(checked) => {
            setAgreeToTerms(checked === true);
            setErrorMsg("");
          }}
          className="mt-1"
        />
        <label
          htmlFor="terms"
          className="text-xs sm:text-sm font-normal ff_poppins"
        >
          I hereby accept the{" "}
          <Link
            target="_blank"
            href={"/policies/terms-conditions"}
            className="text-[#3777FF] underline"
          >
            terms and conditions
          </Link>{" "}
          of the platform and agree to abide by its{" "}
          <Link
            target="_blank"
            href={"/policies/privacy-policy"}
            className="text-[#3777FF] underline"
          >
            policies and guidelines.
          </Link>
        </label>
      </div>

      {errorMsg && (
        <div className="text-red-500 text-sm mb-4 ff_poppins">{errorMsg}</div>
      )}

      <div className="flex gap-3 pt-1 flex-col sm:flex-row">
        <button
          onClick={onBack}
          className="border-2 border-primary py-2 sm:py-3 px-[56px] text-primary font-medium ff_poppins text-lg md:text-xl rounded-full"
        >
          Back
        </button>
        <motion.button
          onClick={onSubmit}
          disabled={!allUploaded || submitting}
          whileHover={allUploaded ? { scale: 1.02 } : {}}
          whileTap={allUploaded ? { scale: 0.98 } : {}}
          className={`flex-[2] py-3 sm:py-4 rounded-full font-medium text-lg md:text-xl ff_poppins transition-all duration-200 ${
            allUploaded && !submitting
              ? "bg-primary text-white shadow-lg shadow-primary/30"
              : "bg-[#c0c0c0ab] text-white cursor-not-allowed"
          }`}
        >
          {submitting ? "Submitting..." : "Submit Application"}
        </motion.button>
      </div>
    </div>
  );
}

// ─── Step 3 — Submitted ───────────────────────────────────────────────────────

function Step3({
  coursesCards,
  selectedCampus,
  selectedIntakeMonth,
  onClose,
}: {
  coursesCards?: CourseItem;
  selectedCampus: string;
  selectedIntakeMonth?: string;
  onClose: () => void;
}) {
  const programValue = useMemo(() => {
    if (coursesCards?.courseName && coursesCards?.universitiesName) {
      return `${coursesCards.courseName} at ${coursesCards.universitiesName}`;
    }
    return coursesCards?.courseName || coursesCards?.universitiesName || "";
  }, [coursesCards?.courseName, coursesCards?.universitiesName]);

  return (
    <div className="space-y-6 pt-2">
      {/* Success banner */}
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 22 }}
        className="text-center px-4"
      >
        <p className="text-xl sm:text-2xl font-bold text-black mb-1.5 text-center ff_poppins">
          Successfully Submitted to Edvia !
        </p>
        <p className="text-xs sm:text-sm ff_poppins text-[#4A4A4A] text-center leading-relaxed">
          Your application is under review
          <br />
          with edvia admissions team.
        </p>
      </motion.div>

      {/* What happens next card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="bg-white border border-[#E0D7FF] rounded-[16px] overflow-hidden shadow-sm"
      >
        <div className="p-5 sm:p-6 pb-4">
          <div className="flex items-center gap-3.5">
            <div className="size-[44px] sm:size-[50px] flex items-center justify-center bg-[#FFDCC2] rounded-[10px] flex-shrink-0">
              <Clock className="size-[22px] sm:size-[26px] text-[#EB9757]" />
            </div>
            <div>
              <p className="text-base sm:text-lg font-semibold ff_poppins text-black leading-tight">
                What happens next?
              </p>
              <p className="text-xs sm:text-sm ff_poppins text-[#7B7B7B] mt-0.5">
                Our admissions team at Edvia will share an update
              </p>
            </div>
          </div>

          <div className="mt-3.5">
            <span className="inline-flex items-center gap-2 rounded-[8px] bg-[#DDEBE1] py-1.5 px-3">
              <Zap className="size-4 text-[#2F7D5B]" />
              <span className="text-xs sm:text-sm font-semibold ff_poppins text-[#2F7D5B]">
                Within 24 hours or less
              </span>
            </span>
          </div>

          {/* Details table */}
          <div className="border-t border-[#EAE6FF] mt-5 pt-3.5 space-y-2">
            <SummaryRow label="Program" value={programValue} />
            {selectedIntakeMonth && (
              <SummaryRow label="Intake" value={selectedIntakeMonth} />
            )}
            {selectedCampus && (
              <SummaryRow label="Campus" value={selectedCampus} />
            )}
          </div>
        </div>

        {/* Confirmation banner */}
        <div className="w-full flex items-center gap-2.5 bg-[#FFDCC2] py-2.5 px-4 text-[#5C5C5C]">
          <Send className="size-4 text-[#5C5C5C] flex-shrink-0" />
          <p className="text-xs sm:text-sm ff_poppins">
            A confirmation has been sent to your registered Email & Whatsapp
          </p>
        </div>
      </motion.div>

      {/* Done button */}
      <div className="flex items-center justify-center pt-2">
        <motion.button
          onClick={onClose}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="bg-primary text-white text-base sm:text-lg font-medium ff_poppins py-2.5 sm:py-3 rounded-full px-12 sm:px-16 cursor-pointer hover:bg-primary/90 transition-all shadow-md"
        >
          Done
        </motion.button>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label?: string; value?: string }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1">
      <span className="text-xs sm:text-sm text-[#7B7B7B] ff_poppins flex-shrink-0">
        {label}
      </span>
      <span className="text-xs sm:text-sm font-bold ff_poppins text-black text-right">
        {value}
      </span>
    </div>
  );
}

// ─── Main popup ───────────────────────────────────────────────────────────────

export default function ApplyPopUp({
  popUp,
  setPopUp,
  coursesCards,
}: {
  coursesCards?: CourseItem;
  popUp: boolean;
  setPopUp: (v: boolean) => void;
}) {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { showToast } = useToastNotification();

  const { academicDetails, englishProficiencyDetails } = useAppSelector(
    (state) => state.course,
  );
  const {
    academicDetails: aiAcademicDetails,
    englishProficiencyDetails: aiEnglishProficiencyDetails,
    savedEligibility,
  } = useAppSelector((state) => state.aiCourseFinder);

  const resolvedEnglishProficiencyDetails = useMemo(() => {
    const test =
      aiEnglishProficiencyDetails?.test ||
      englishProficiencyDetails?.test ||
      String(savedEligibility?.test || "") ||
      "";
    const selectBoard =
      aiEnglishProficiencyDetails?.selectBoard ||
      englishProficiencyDetails?.selectBoard ||
      String(savedEligibility?.boardName || "") ||
      "";
    const overallScore =
      aiEnglishProficiencyDetails?.overallScore ||
      englishProficiencyDetails?.overallScore ||
      String(savedEligibility?.overall || "") ||
      "";
    const englishMarks =
      aiEnglishProficiencyDetails?.englishMarks ||
      englishProficiencyDetails?.englishMarks ||
      String(savedEligibility?.overall || "") ||
      "";

    return {
      ...englishProficiencyDetails,
      ...aiEnglishProficiencyDetails,
      test,
      selectBoard,
      overallScore,
      englishMarks,
    };
  }, [englishProficiencyDetails, aiEnglishProficiencyDetails, savedEligibility]);

  const resolvedAcademicDetails = useMemo(() => {
    const pastQualification =
      academicDetails?.pastQualification ||
      aiAcademicDetails?.pastQualification ||
      String(savedEligibility?.pastQualification || "") ||
      "";
    const pastGrades =
      academicDetails?.pastGrades ||
      aiAcademicDetails?.pastGrades ||
      String(savedEligibility?.pastQualificationPercentage || "") ||
      "";
    const passingYear =
      academicDetails?.passingYear ||
      aiAcademicDetails?.passingYear ||
      String(savedEligibility?.qualificationYear || "") ||
      "";

    return {
      ...academicDetails,
      ...aiAcademicDetails,
      pastQualification,
      pastGrades,
      passingYear,
    };
  }, [academicDetails, aiAcademicDetails, savedEligibility]);

  const { user, loading: authLoading } = useAuth();
  const { documents: userDocument, userDetails, userLoading } = useAppSelector(
    (state) => state.userProfile,
  );
  const isProfileLoading = authLoading || (Boolean(user?.uid) && userLoading && userDetails === null);
  const isEmailVerified = userDetails ? (userDetails.email_verified && Boolean(user?.emailVerified)) : true;

  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [selectedIntake, setSelectedIntake] = useState<number | null>(null);
  const [selectedCampus, setSelectedCampus] = useState("");
  const [selectedCampusDetails, setSelectedCampusDetails] =
    useState<CampusDetails | null>(null);
  const [campusLoading, setCampusLoading] = useState(false);

  const [documents, setDocuments] = useState<DocumentType[]>([]);
  const [noEnglishTest, setNoEnglishTest] = useState(false);
  const [selectedHighestQualification, setSelectedHighestQualification] =
    useState("12th");
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [uploadOverlayOpen, setUploadOverlayOpen] = useState(false);
  const [currentDocumentId, setCurrentDocumentId] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [activeTab, setActiveTab] = useState<string>("");
  const [forceShowVerification, setForceShowVerification] =
    useState<boolean>(false);
  const [deletingDocType, setDeletingDocType] = useState<string>("");
  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean;
    docType: string;
    docName: string;
  }>({ isOpen: false, docType: "", docName: "" });
  const [showVerifyEmail, setShowVerifyEmail] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showMismatchAlert, setShowMismatchAlert] = useState(false);
  const [mismatchDetails, setMismatchDetails] = useState<string[]>([]);
  const [mismatchAccepted, setMismatchAccepted] = useState(false);
  const [eligibilityResult, setEligibilityResult] = useState<{
    isEligible: boolean;
    reasons: string[];
  } | null>(null);
  const [showEligibilityWarning, setShowEligibilityWarning] = useState(false);

  const optionalDocumentTypes = useMemo(() => new Set(["moi_certificate"]), []);

  const {
    jobs,
    startExtraction,
    setJobState,
    removeJob,
    submitVerification,
    updateExtractedData,
  } = useParallelDocumentUpload(coursesCards?.course_id, user?.uid);

  // Sync documents and user metadata
  useEffect(() => {
    if (user?.uid && popUp) {
      dispatch(getUploadedDocument(user.uid));
    }
  }, [user?.uid, dispatch, popUp]);

  const handleCampusSelect = async (campusName: string) => {
    setSelectedCampus(campusName);
    setErrorMsg("");
    if (!campusName) {
      setSelectedCampusDetails(null);
      return;
    }
    setCampusLoading(true);
    const formattedCampusName = campusName.replace("-", " ").trim();
    try {
      const response = await getCampusDetails(formattedCampusName);
      setSelectedCampusDetails(
        Object.keys(response).length > 0 ? response : null,
      );
    } catch (error) {
      console.error("Error fetching campus details:", error);
      setSelectedCampusDetails(null);
    } finally {
      setCampusLoading(false);
    }
  };

  const normalizeQualificationTitle = useCallback((value: string) => {
    const normalized = (value || "")
      .toLowerCase()
      .trim()
      .replace(/['\u2019]/g, "")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();

    if (!normalized) return "";
    if (
      normalized.includes("pg diploma") ||
      normalized.includes("post graduate diploma")
    )
      return "pg_diploma";
    if (normalized.includes("undergraduate") || normalized.includes("bachelor"))
      return "bachelor";
    if (normalized.includes("postgraduate") || normalized.includes("master"))
      return "master";
    if (normalized.includes("phd") || normalized.includes("doctorate"))
      return "phd";
    if (normalized.includes("diploma")) return "diploma";
    if (
      normalized.includes("certification") ||
      normalized.includes("certificate")
    )
      return "certification";
    if (normalized.startsWith("12") || normalized.includes("senior secondary"))
      return "12th";
    if (normalized.startsWith("10") || normalized.includes("secondary"))
      return "10th";
    return normalized.replace(/\s+/g, "_");
  }, []);

  const getProfileHighestQualification = useCallback(() => {
    const educationDetails = userDetails?.education_details;
    if (!Array.isArray(educationDetails) || educationDetails.length === 0)
      return "";

    const getPriority = (title: string) => {
      const normalized = normalizeQualificationTitle(title);
      const matched = qualificationOptions.find(
        (option) => normalizeQualificationTitle(option.title) === normalized,
      );
      return matched?.priority ?? Infinity;
    };

    const highest = educationDetails.reduce((prev: any, current: any) =>
      getPriority(current?.qualification_level) <
      getPriority(prev?.qualification_level)
        ? current
        : prev,
    );

    return highest?.qualification_level || "";
  }, [normalizeQualificationTitle, userDetails?.education_details]);

  const isDocumentUploaded = useCallback(
    (documentType: string) => {
      if (userDocument && Array.isArray(userDocument)) {
        return userDocument.some((doc) => doc.doc_type === documentType);
      }
      return false;
    },
    [userDocument],
  );

  const getUploadedDocumentByType = useCallback(
    (documentType: string) => {
      if (!Array.isArray(userDocument)) return null;
      return userDocument.find((doc) => doc.doc_type === documentType) ?? null;
    },
    [userDocument],
  );

  const isVerifiedDocument = (status?: string) => {
    const normalizedStatus = (status || "").toLowerCase();
    return normalizedStatus === "verified" || normalizedStatus === "completed";
  };

  const canDeleteUploadedDocument = (documentType: string) => {
    const existingDoc = getUploadedDocumentByType(documentType);
    return !!existingDoc?.doc_id && !isVerifiedDocument(existingDoc.doc_status);
  };

  const isDocumentCompleted = (doc: DocumentType) => {
    const jobStatus = jobs[doc.id]?.status;
    return doc.status || jobStatus === "completed";
  };

  const getDocumentPriority = (docId: string) => {
    const normalized = (docId || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");

    const tabPriority: Record<string, number> = {
      phd_certificate: 10,
      phd_transcript: 11,
      master_marksheet: 20,
      master_degree: 20,
      master_certificate: 20,
      master_transcript: 21,
      pg_diploma_marksheet: 30,
      pg_diploma_degree: 30,
      pg_diploma_certificate: 30,
      pg_diploma_transcript: 31,
      bachelor_marksheet: 40,
      bachelor_certificate: 40,
      bachelor_transcript: 41,
      diploma_marksheet: 50,
      diploma_degree: 50,
      diploma_certificate: 50,
      diploma_transcript: 51,
      "12th_marksheet": 60,
      "10th_marksheet": 70,
      english_proficiency: 800,
      moi_certificate: 810,
      passport: 900,
    };

    if (tabPriority[normalized] !== undefined) return tabPriority[normalized];

    const isTranscript = normalized.includes("transcript");
    if (normalized.includes("phd")) return isTranscript ? 11 : 10;
    if (normalized.includes("master")) return isTranscript ? 21 : 20;
    if (normalized.includes("pg_diploma") || normalized.includes("pgdiploma"))
      return isTranscript ? 31 : 30;
    if (normalized.includes("bachelor")) return isTranscript ? 41 : 40;
    if (normalized.includes("diploma")) return isTranscript ? 51 : 50;
    if (normalized.includes("12th")) return 60;
    if (normalized.includes("10th")) return 70;
    if (normalized.includes("english")) return 800;
    if (normalized.includes("moi")) return 810;
    if (normalized.includes("passport")) return 900;

    return 1000;
  };

  useEffect(() => {
    const testType = resolvedEnglishProficiencyDetails?.test;
    if (isNoEnglishTest(testType)) {
      setNoEnglishTest(true);
    } else if (testType) {
      setNoEnglishTest(false);
    } else if (isDocumentUploaded("moi_certificate")) {
      setNoEnglishTest(true);
    } else if (isDocumentUploaded("english_proficiency")) {
      setNoEnglishTest(false);
    }
  }, [
    userDocument,
    resolvedEnglishProficiencyDetails?.test,
    isDocumentUploaded,
  ]);

  useEffect(() => {
    if (resolvedAcademicDetails?.pastQualification) {
      setSelectedHighestQualification(resolvedAcademicDetails.pastQualification);
    } else {
      const profileHighestQualification = getProfileHighestQualification();
      setSelectedHighestQualification(profileHighestQualification || "12th");
    }
  }, [resolvedAcademicDetails?.pastQualification, getProfileHighestQualification]);

  useEffect(() => {
    if (userDocument === undefined || userDocument === null) return;

    const selectedQualification = normalizeQualificationTitle(
      selectedHighestQualification,
    );
    const matched = academicRecords.find(
      (item) =>
        normalizeQualificationTitle(item.title) === selectedQualification,
    );
    const requiredOptions = matched?.options || [];

    const baseDocuments = [
      {
        id: "passport",
        name: "Passport",
        status: false,
        disabled: false,
        optional: false,
      },
    ];

    if (noEnglishTest) {
      baseDocuments.push({
        id: "moi_certificate",
        name: "Medium of Instruction",
        status: false,
        disabled: false,
        optional: true,
      });
    } else {
      baseDocuments.push({
        id: "english_proficiency",
        name: "English Proficiency",
        status: false,
        disabled: false,
        optional: false,
      });
    }

    baseDocuments.push(
      {
        id: "10th_marksheet",
        name: "10th Marksheet",
        status: false,
        disabled: false,
        optional: false,
      },
      {
        id: "12th_marksheet",
        name: "12th Marksheet",
        status: false,
        disabled: false,
        optional: false,
      },
    );

    const requiredDocuments = [...baseDocuments];

    requiredOptions.forEach((opt) => {
      if (!requiredDocuments.some((d) => d.id === opt.value)) {
        requiredDocuments.push({
          id: opt.value,
          name: opt.title,
          status: false,
          disabled: false,
          optional: optionalDocumentTypes.has(opt.value),
        });
      }
    });

    const orderedRequiredDocuments = [...requiredDocuments].sort((a, b) => {
      const priorityA = getDocumentPriority(a.id);
      const priorityB = getDocumentPriority(b.id);
      if (priorityA === priorityB) return a.name.localeCompare(b.name);
      return priorityA - priorityB;
    });

    if (Array.isArray(userDocument)) {
      setDocuments(
        orderedRequiredDocuments.map((doc) => {
          const existingDoc = userDocument.find(
            (userDoc) => userDoc.doc_type === doc.id,
          );
          return existingDoc ? { ...doc, status: true, disabled: true } : doc;
        }),
      );
    } else {
      setDocuments(orderedRequiredDocuments);
    }
  }, [
    selectedHighestQualification,
    noEnglishTest,
    userDocument,
    optionalDocumentTypes,
    normalizeQualificationTitle,
  ]);

  // Sync profile details mismatch details
  useEffect(() => {
    if (!resolvedAcademicDetails && !resolvedEnglishProficiencyDetails) return;

    const hasEducation = (userDetails?.education_details?.length ?? 0) > 0;
    const hasEnglishTest =
      (userDetails?.english_proficiency_test?.length ?? 0) > 0;

    const normalize = (val: string) => {
      if (!val) return "";
      const v = val
        .toLowerCase()
        .trim()
        .replace(/[''\u2019]s$/, "");
      if (v === "bachelor") return "bachelor";
      if (v === "master") return "master";
      return v;
    };

    const getPriority = (title: string) => {
      const normalized = normalize(title);
      const opt = qualificationOptions.find(
        (o) => normalize(o.title) === normalized,
      );
      return opt?.priority ?? Infinity;
    };

    const diffs: string[] = [];

    if (resolvedAcademicDetails?.pastQualification && hasEducation) {
      const profileHighest = userDetails!.education_details!.reduce(
        (highest: any, current: any) =>
          getPriority(current.qualification_level) <
          getPriority(highest.qualification_level)
            ? current
            : highest,
        userDetails!.education_details![0],
      );

      if (profileHighest) {
        const searchQual = normalize(resolvedAcademicDetails.pastQualification);
        const profileQual = normalize(profileHighest.qualification_level);

        if (searchQual && profileQual && searchQual !== profileQual) {
          diffs.push(
            `Qualification: "${resolvedAcademicDetails.pastQualification}" in search vs "${profileHighest.qualification_level}" in profile`,
          );
        }

        if (
          resolvedAcademicDetails.pastGrades &&
          profileHighest.cgpa_or_percentage_value
        ) {
          const searchGrades = String(resolvedAcademicDetails.pastGrades).trim();
          const profileGrades = String(
            profileHighest.cgpa_or_percentage_value,
          ).trim();
          if (searchGrades !== profileGrades) {
            diffs.push(
              `Grades: "${searchGrades}" in search vs "${profileGrades}" in profile`,
            );
          }
        }
      }
    }

    if (
      resolvedEnglishProficiencyDetails?.test &&
      !isNoEnglishTest(resolvedEnglishProficiencyDetails.test) &&
      hasEnglishTest
    ) {
      const profileTest = userDetails!.english_proficiency_test![0];
      const searchTest = normalize(resolvedEnglishProficiencyDetails.test);
      const profileTestName = normalize(profileTest?.testName || "");

      if (searchTest && profileTestName && searchTest !== profileTestName) {
        diffs.push(
          `English Test: "${resolvedEnglishProficiencyDetails.test}" in search vs "${profileTest?.testName}" in profile`,
        );
      }

      if (resolvedEnglishProficiencyDetails.overallScore && profileTest?.overall) {
        const searchOverall = String(
          resolvedEnglishProficiencyDetails.overallScore,
        ).trim();
        const profileOverall = String(profileTest.overall).trim();
        if (searchOverall !== profileOverall) {
          diffs.push(
            `Overall Score: "${searchOverall}" in search vs "${profileOverall}" in profile`,
          );
        }
      }
    }

    if (diffs.length > 0) {
      setMismatchDetails(diffs);
      setMismatchAccepted(false);
    } else {
      setMismatchDetails([]);
      setShowMismatchAlert(false);
    }
  }, [userDetails, resolvedAcademicDetails, resolvedEnglishProficiencyDetails]);

  const handleCheckboxChange = (checked: boolean) => {
    if (checked) {
      if (isDocumentUploaded("english_proficiency")) {
        showToast(
          "Please delete the English Proficiency document before selecting this option.",
          "error",
        );
        return;
      }
      setNoEnglishTest(true);
    } else {
      if (isDocumentUploaded("moi_certificate")) {
        showToast(
          "Please delete the MOI certificate before adding an English test.",
          "error",
        );
        return;
      }
      setNoEnglishTest(false);
    }
  };

  const openUploadOverlay = (documentId: string) => {
    const doc = documents.find((d) => d.id === documentId);
    if (doc && doc.disabled) return;
    setCurrentDocumentId(documentId);
    setUploadOverlayOpen(true);
  };

  const closeUploadOverlay = () => {
    setUploadOverlayOpen(false);
    setCurrentDocumentId("");
  };

  const markDocumentAsUploaded = (documentId: string) => {
    setDocuments((prev) =>
      prev.map((doc) =>
        doc.id === documentId ? { ...doc, status: true, disabled: true } : doc,
      ),
    );
  };

  const handleDocumentFileAccepted = async (file: File) => {
    const documentId = currentDocumentId;
    const existingJob = jobs[documentId];

    if (
      existingJob &&
      (existingJob.status === "extracting" ||
        existingJob.status === "uploading" ||
        existingJob.status === "verification_pending")
    ) {
      return;
    }

    closeUploadOverlay();
    const uploadStatus = await startExtraction(
      documentId,
      getCurrentDocumentName(),
      file,
    );
    if (uploadStatus === "completed") markDocumentAsUploaded(documentId);
  };

  const getCurrentDocumentName = () => {
    const doc = documents.find((d) => d.id === currentDocumentId);
    return doc ? doc.name : "";
  };

  const handleDeleteDocument = async (documentType: string) => {
    const existingDoc = getUploadedDocumentByType(documentType);
    if (!existingDoc?.doc_id || isVerifiedDocument(existingDoc.doc_status))
      return;

    setDeletingDocType(documentType);
    try {
      await axiosInstance.delete(`/documents/delete/${existingDoc.doc_id}`);
      showToast("Document deleted successfully", "success");
      setDocuments((prev) =>
        prev.map((doc) =>
          doc.id === documentType
            ? { ...doc, status: false, disabled: false }
            : doc,
        ),
      );
      if (jobs[documentType]) removeJob(documentType);
      if (activeTab === documentType) {
        setForceShowVerification(false);
        setActiveTab("");
      }
      if (user?.uid) {
        void dispatch(getUploadedDocument(user.uid));
        void dispatch(getUserDetail(user.uid));
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      showToast("Failed to delete document. Please try again.", "error");
    } finally {
      setDeletingDocType("");
      setDeleteModalState({ isOpen: false, docType: "", docName: "" });
    }
  };

  const checkEligibilityForCriterion = (
    id: string,
    course: CourseItem,
  ): { isEligible: boolean; reason?: string } => {
    const normalizeQualificationLevel = (value?: string) => {
      const normalized = (value || "")
        .toLowerCase()
        .trim()
        .replace(/['\u2019]s$/, "");
      if (normalized === "bachelors" || normalized === "undergraduate")
        return "bachelor";
      if (normalized === "masters" || normalized === "postgraduate")
        return "master";
      return normalized;
    };

    const qualificationRank: Record<string, number> = {
      "10th": 1,
      secondary: 1,
      ssc: 1,
      "12th": 2,
      "senior secondary": 2,
      hsc: 2,
      diploma: 3,
      bachelor: 4,
      ug: 4,
      master: 5,
      pg: 5,
      phd: 6,
      doctorate: 6,
    };

    const getQualificationRank = (value?: string) =>
      qualificationRank[normalizeQualificationLevel(value)] ?? 0;

    const isNullishOrMissing = (obj: any, keyPath: string[]): boolean => {
      let val = obj;
      for (const key of keyPath) {
        if (val == null || !(key in val)) return true;
        val = val[key];
      }
      return val == null;
    };

    switch (id) {
      case "pastQualification": {
        const courseLevel = normalizeQualificationLevel(
          course.course_details?.course_level,
        );
        const pastQual = normalizeQualificationLevel(
          resolvedAcademicDetails?.pastQualification,
        );

        if (pastQual === "12th") {
          const validLevels = ["bachelor", "diploma", "certification"];
          if (!validLevels.includes(courseLevel || "")) {
            return {
              isEligible: false,
              reason: `Your qualification (12th) is not sufficient for ${courseLevel} level courses. Valid course levels: Bachelor's, Diploma, or Certification.`,
            };
          }
        } else if (pastQual === "bachelor") {
          const validLevels = [
            "bachelor",
            "master",
            "diploma",
            "certification",
          ];
          if (!validLevels.includes(courseLevel || "")) {
            return {
              isEligible: false,
              reason: `Your qualification is not sufficient for ${courseLevel} level courses. Valid course levels: Master's, Diploma, or Certification.`,
            };
          }
        }

        if (isNullishOrMissing(course, ["pre_requisite_details", "level"])) {
          return { isEligible: true };
        }

        const requiredQualification = course.pre_requisite_details?.level || "";
        const requiredRank = getQualificationRank(requiredQualification);
        const studentRank = getQualificationRank(
          resolvedAcademicDetails?.pastQualification,
        );

        if (requiredRank > 0 && studentRank > 0 && studentRank < requiredRank) {
          return {
            isEligible: false,
            reason: `Required qualification level: ${requiredQualification}.`,
          };
        }

        return { isEligible: true };
      }

      case "twelfthPercentage": {
        if (isNullishOrMissing(course, ["twelfth_percentage"]))
          return { isEligible: true };

        const studentPercentage = parseFloat(resolvedAcademicDetails?.pastGrades || "0");
        const requiredPercentage = course.twelfth_percentage;

        if (studentPercentage < requiredPercentage) {
          return {
            isEligible: false,
            reason: `Your 12th percentage (${studentPercentage}%) is below the required ${requiredPercentage}%.`,
          };
        }

        return { isEligible: true };
      }

      case "boardName": {
        if (
          isNullishOrMissing(course, ["state_board_not_accepted"]) ||
          !course.state_board_not_accepted?.length
        ) {
          return { isEligible: true };
        }

        const selectedBoard = resolvedEnglishProficiencyDetails?.selectBoard || "";
        const isNotAccepted =
          course.state_board_not_accepted.includes(selectedBoard);

        if (isNotAccepted) {
          return {
            isEligible: false,
            reason: `Your board (${selectedBoard}) is not accepted for this course.`,
          };
        }

        return { isEligible: true };
      }

      case "english": {
        if (
          isNullishOrMissing(course, ["twelfth_english_percentage"]) &&
          isNullishOrMissing(course, ["englishProficiencyRequirements"])
        ) {
          return { isEligible: true };
        }

        if (isNoEnglishTest(resolvedEnglishProficiencyDetails?.test)) {
          if (!course.english_waiver_accepted) {
            return {
              isEligible: false,
              reason: "English waiver is not accepted for this course.",
            };
          }

          if (course.english_waiver_state_boards_accepted) {
            try {
              const acceptedBoards =
                typeof course.english_waiver_state_boards_accepted === "string"
                  ? JSON.parse(course.english_waiver_state_boards_accepted)
                  : course.english_waiver_state_boards_accepted;
              if (
                Array.isArray(acceptedBoards) &&
                resolvedEnglishProficiencyDetails?.selectBoard
              ) {
                const boardName =
                  resolvedEnglishProficiencyDetails.selectBoard.toLowerCase();
                const isBoardAccepted = acceptedBoards.some(
                  (board: string) =>
                    board.toLowerCase().includes(boardName) ||
                    boardName.includes(board.toLowerCase()),
                );

                if (!isBoardAccepted) {
                  return {
                    isEligible: false,
                    reason: `Your board (${resolvedEnglishProficiencyDetails.selectBoard}) is not accepted for an English waiver for this course.`,
                  };
                }
              }
            } catch (e) {
              console.error(
                "Failed to parse english_waiver_state_boards_accepted",
                e,
              );
            }
          }

          const studentMarks = parseFloat(
            resolvedEnglishProficiencyDetails?.englishMarks || "0",
          );
          let requiredMarks = course.twelfth_english_percentage as any;
          if (typeof requiredMarks === "object" && requiredMarks !== null) {
            const boardName = resolvedEnglishProficiencyDetails?.selectBoard || "";
            const boardValue = requiredMarks[boardName];
            if (boardValue !== undefined && boardValue !== null) {
              requiredMarks = Number(boardValue);
            } else {
              const values = Object.values(requiredMarks).map(Number).filter(v => !isNaN(v));
              requiredMarks = values.length > 0 ? Math.min(...values) : 0;
            }
          } else {
            requiredMarks = Number(requiredMarks) || 0;
          }

          if (requiredMarks > 0 && requiredMarks <= 1) {
            requiredMarks = requiredMarks * 100;
          }

          if (
            !isNullishOrMissing(course, ["twelfth_english_percentage"]) &&
            studentMarks < requiredMarks
          ) {
            return {
              isEligible: false,
              reason: `Your 12th English marks (${studentMarks}%) are below the required ${requiredMarks}%.`,
            };
          }

          return { isEligible: true };
        }

        const testType = resolvedEnglishProficiencyDetails?.test;
        if (!testType) return { isEligible: true };

        const requirement = course.englishProficiencyRequirements?.[testType];
        if (!requirement) return { isEligible: true };

        const studentScores = {
          Overall: parseFloat(resolvedEnglishProficiencyDetails?.overallScore || "0"),
          Reading: parseFloat(resolvedEnglishProficiencyDetails?.readingScore || "0"),
          Writing: parseFloat(resolvedEnglishProficiencyDetails?.writingScore || "0"),
          Speaking: parseFloat(resolvedEnglishProficiencyDetails?.speakingScore || "0"),
          Listening: parseFloat(
            resolvedEnglishProficiencyDetails?.listeningScore || "0",
          ),
        };

        const failedSections = [];
        if (studentScores.Overall < (requirement.Overall ?? 0))
          failedSections.push(
            `Overall: ${studentScores.Overall} (required: ${requirement.Overall})`,
          );
        if (studentScores.Reading > 0 && studentScores.Reading < (requirement.Reading ?? 0))
          failedSections.push(
            `Reading: ${studentScores.Reading} (required: ${requirement.Reading})`,
          );
        if (studentScores.Writing > 0 && studentScores.Writing < (requirement.Writing ?? 0))
          failedSections.push(
            `Writing: ${studentScores.Writing} (required: ${requirement.Writing})`,
          );
        if (studentScores.Speaking > 0 && studentScores.Speaking < (requirement.Speaking ?? 0))
          failedSections.push(
            `Speaking: ${studentScores.Speaking} (required: ${requirement.Speaking})`,
          );
        if (studentScores.Listening > 0 && studentScores.Listening < (requirement.Listening ?? 0))
          failedSections.push(
            `Listening: ${studentScores.Listening} (required: ${requirement.Listening})`,
          );

        if (failedSections.length > 0) {
          return {
            isEligible: false,
            reason: `${resolvedEnglishProficiencyDetails?.test} scores below requirement: ${failedSections.join(", ")}.`,
          };
        }

        return { isEligible: true };
      }

      case "education_gap": {
        if (!resolvedAcademicDetails?.passingYear) return { isEligible: true };

        const currentYear = new Date().getFullYear();
        const passingYear = parseInt(resolvedAcademicDetails.passingYear);

        if (isNaN(passingYear) || passingYear > currentYear) {
          return {
            isEligible: false,
            reason: `Invalid passing year: ${academicDetails.passingYear}.`,
          };
        }

        const calculatedGap = currentYear - passingYear;
        const allowedGap = course.gap_allowed ?? calculatedGap;

        if (calculatedGap > allowedGap) {
          return {
            isEligible: false,
            reason: `Your education gap (${calculatedGap} years) exceeds the maximum allowed gap of ${allowedGap} years.`,
          };
        }

        return { isEligible: true };
      }

      default:
        return { isEligible: true };
    }
  };

  const handleUseProfileDetails = async () => {
    setShowMismatchAlert(false);
    setMismatchDetails([]);
    setPopUp(false);
  };

  const handleSubmitApplication = async () => {
    if (!coursesCards) return;

    void sendCustomGaEvent("application_requirements_submit_attempt", {
      course_id: coursesCards.course_id || "",
      has_selected_intake: selectedIntake !== null,
      documents_count: documents.length,
    });

    if (selectedIntake === null) {
      setErrorMsg("Please select an intake.");
      return;
    }

    const campusOptionsCount = coursesCards.campusDetails
      ? Object.keys(coursesCards.campusDetails).length
      : coursesCards.campus_name
        ? coursesCards.campus_name.split(",").filter(Boolean).length
        : 0;

    if (campusOptionsCount > 0 && !selectedCampus) {
      setErrorMsg("Please select a campus.");
      return;
    }

    if (documents.some((doc) => !doc.optional && !isDocumentCompleted(doc))) {
      setErrorMsg("Please upload all required documents.");
      return;
    }

    if (!agreeToTerms) {
      setErrorMsg("Please agree to the terms and conditions.");
      return;
    }

    if (mismatchDetails.length > 0 && !mismatchAccepted) {
      setShowMismatchAlert(true);
      return;
    }

    const criteriaIds = [
      "pastQualification",
      "twelfthPercentage",
      "boardName",
      "english",
      "education_gap",
    ];
    const failedReasons: string[] = [];

    for (const id of criteriaIds) {
      const result = checkEligibilityForCriterion(id, coursesCards);
      if (!result.isEligible && result.reason)
        failedReasons.push(result.reason);
    }

    if (failedReasons.length > 0) {
      setEligibilityResult({ isEligible: false, reasons: failedReasons });
      setShowEligibilityWarning(true);
      return;
    }

    const selectedCourseCountryName = getCourseCountryName(coursesCards);
    const countryName =
      getCountryName(selectedCampusDetails?.country) ||
      selectedCourseCountryName;

    if (!countryName) {
      setErrorMsg("Unable to identify the course country. Please try again.");
      return;
    }

    setEligibilityResult(null);
    setSubmitting(true);
    setErrorMsg("");

    const addressInfo = selectedCampusDetails
      ? { city: selectedCampusDetails.city, state: selectedCampusDetails.state }
      : {
          city: coursesCards.address_info?.city,
          state: coursesCards.address_info?.state,
        };

    const payload = {
      studentId: user?.uid ?? "",
      courseId: coursesCards.course_id,
      countryName,
      courseDetails: coursesCards.course_details,
      intakeDetails: {
        month: coursesCards.intake?.month[selectedIntake] || "",
        status: coursesCards.intake?.status[selectedIntake] || "",
        deadline: String(coursesCards.intake?.deadline[selectedIntake] || ""),
      },
      addressInfo,
      collegeName: coursesCards.universitiesName ?? coursesCards.college_name,
      ...(selectedCampus && { campusName: selectedCampus }),
    };

    try {
      const res = await dispatch(applyForCourse(payload));

      if (applyForCourse.fulfilled.match(res)) {
        if (res.payload.status) {
          void sendCustomGaEvent("application_requirements_submit_success", {
            course_id: coursesCards.course_id || "",
          });
          showToast("Application submitted successfully!", "success");
          goTo(2); // Go to Step3
          if (user?.uid) {
            await dispatch(getAppliedApplications(user.uid));
            await dispatch(getAppliedCourse(user.uid));
          }
        } else {
          showToast(res.payload.message ?? "Application failed!", "error");
        }
      }

      if (applyForCourse.rejected.match(res)) {
        void sendCustomGaEvent("application_requirements_submit_failed", {
          course_id: coursesCards.course_id || "",
        });
        showToast("Failed to submit application.", "error");
        console.error("Error payload:", res.payload);
      }
    } catch (error) {
      void sendCustomGaEvent("application_requirements_submit_failed", {
        course_id: coursesCards.course_id || "",
      });
      showToast("Failed to submit application. Please try again.", "error");
      console.error("Caught error:", error);
    } finally {
      setSubmitting(false);
      if (user?.uid) {
        await dispatch(getAppliedCourse(user.uid));
      }
    }
  };

  const hasProgress = useCallback(() => {
    return (
      selectedIntake !== null ||
      documents.some((doc) => isDocumentCompleted(doc) || !!jobs[doc.id]) ||
      agreeToTerms ||
      selectedCampus
    );
  }, [selectedIntake, documents, agreeToTerms, selectedCampus, jobs]);

  const goTo = (next: number) => {
    setDir(next > step ? 1 : -1);
    setStep(next);
  };

  const handleClose = () => {
    void sendCustomGaEvent("application_requirements_popup_close", {
      course_id: coursesCards?.course_id || "",
    });
    if (hasProgress() && step !== 2) {
      setShowConfirmation(true);
    } else {
      closePopupAndReset();
    }
  };

  const closePopupAndReset = () => {
    setPopUp(false);
    setShowConfirmation(false);
    document.body.classList.remove("overflow-hidden");
    setTimeout(() => {
      setStep(0);
      setDir(1);
      setSelectedIntake(null);
      setSelectedCampus("");
      setSelectedCampusDetails(null);
      setAgreeToTerms(false);
      setErrorMsg("");
      setMismatchAccepted(false);
      setEligibilityResult(null);
    }, 400);
  };

  const reviewJobs = Object.values(jobs)
    .filter((j) => j.status === "verification_pending" || j.status === "saving")
    .sort((a, b) => {
      const priorityA = getDocumentPriority(a.docId);
      const priorityB = getDocumentPriority(b.docId);
      if (priorityA === priorityB)
        return a.documentName.localeCompare(b.documentName);
      return priorityA - priorityB;
    });

  const activeReviewJob =
    activeTab &&
    jobs[activeTab] &&
    (jobs[activeTab].status === "verification_pending" ||
      jobs[activeTab].status === "saving")
      ? jobs[activeTab]
      : null;

  useEffect(() => {
    if (
      reviewJobs.length > 0 &&
      (!activeTab ||
        !jobs[activeTab] ||
        (jobs[activeTab].status !== "verification_pending" &&
          jobs[activeTab].status !== "saving"))
    ) {
      setActiveTab(reviewJobs[0].docId);
    }
  }, [jobs, activeTab, reviewJobs]);

  const isAnyProcessing = Object.values(jobs).some(
    (j) => j.status === "extracting" || j.status === "uploading",
  );

  const allUploadedOrPending = documents.every(
    (d) =>
      d.optional ||
      d.status ||
      (jobs[d.id] &&
        (jobs[d.id].status === "verification_pending" ||
          jobs[d.id].status === "saving")) ||
      (jobs[d.id] && jobs[d.id].status === "completed"),
  );

  const shouldShowVerifyModal =
    reviewJobs.length > 0 &&
    (forceShowVerification || (!isAnyProcessing && allUploadedOrPending));

  const selectedIntakeMonthFormatted = useMemo(() => {
    if (
      selectedIntake !== null &&
      coursesCards?.intake?.month[selectedIntake]
    ) {
      return formatMonthYear(coursesCards.intake.month[selectedIntake]);
    }
    return "";
  }, [selectedIntake, coursesCards?.intake?.month]);

  return (
    <>
      {/* Backdrop */}
      <AnimatePresence>
        {popUp && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          />
        )}
      </AnimatePresence>

      {/* Sheet */}
      <motion.div
        className="fixed bottom-0 left-0 right-0 z-50 bg-[#F7F5FF] rounded-t-[32px] shadow-[0px_-8px_40px_rgba(0,0,0,0.15)] max-h-[92vh] overflow-y-auto flex flex-col"
        initial={false}
        animate={popUp ? { y: 0 } : { y: "105%" }}
        transition={{ type: "spring", stiffness: 340, damping: 38 }}
      >
        {isProfileLoading ? (
          <div className="w-full min-h-[300px] flex items-center justify-center p-8">
            <Loader className="size-8 text-primary animate-spin" />
          </div>
        ) : !isEmailVerified ? (
          <div className="relative w-full px-5 sm:px-8 md:px-12 py-8 flex flex-col items-center justify-center">
            <button
              onClick={handleClose}
              className="absolute top-5 right-5 sm:right-8 w-[46.67px] h-[46.67px] bg-[#E8E3FF] flex items-center justify-center rounded-full hover:bg-primary/20 transition-colors z-20 cursor-pointer"
            >
              <X className="size-5 text-primary" />
            </button>
            <div className="relative z-10 flex w-full max-w-[360px] flex-col items-center rounded-[24px] px-5 py-6 text-center sm:rounded-[28px] sm:px-8 sm:py-7">
              <Image
                className="w-full max-w-[180px] sm:max-w-[220px]"
                width={300}
                height={400}
                alt="email-verify"
                src={"/assets/images/svg/emailVerify.svg"}
              />
              <p className="mt-4 text-center text-lg font-semibold text-black sm:text-[28px] sm:leading-[1.2]">
                One last step before you apply!
              </p>
              <p className="ff_poppins mt-3 text-center text-sm font-medium text-[#4A5565] sm:text-base">
                Please verify your email address to submit your application.
              </p>
              <button
                type="button"
                onClick={() => setShowVerifyEmail(true)}
                className="ff_poppins mt-5 cursor-pointer text-sm font-semibold text-[#5E57EB] underline underline-offset-4 transition hover:text-[#4B45C6] sm:text-base"
              >
                Verify Now
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-y-auto flex-1 px-5 sm:px-8 md:px-12 pb-10 pt-4">
            {/* Header */}
            <div className="flex items-start justify-between pb-4 border-b border-[#D0C4FF]">
              <div>
                <p className="text-[10px] font-bold tracking-widest text-primary ff_poppins uppercase">
                  Application
                </p>
                <p className="font-bold text-lg sm:text-xl text-[#1a1a2e] mt-0.5 leading-tight">
                  {coursesCards?.courseName}
                </p>
                <p className="text-sm font-medium text-[#939393] ff_poppins mt-0.5">
                  {coursesCards?.universitiesName}
                </p>
              </div>
              <button
                onClick={handleClose}
                className="w-[46.67px] h-[46.67px] bg-[#E8E3FF] flex items-center justify-center rounded-full hover:bg-primary/20 transition-colors mt-0.5"
              >
                <X className="size-5 text-primary" />
              </button>
            </div>

            {/* Step bar */}
            <StepBar current={step} />

            {/* Step content */}
            <div className="mt-3 relative overflow-hidden">
              <AnimatePresence mode="wait" custom={dir}>
                <motion.div
                  key={step}
                  custom={dir}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
                >
                  {step === 0 && (
                    <Step1
                      coursesCards={coursesCards}
                      selectedIntake={selectedIntake}
                      setSelectedIntake={setSelectedIntake}
                      selectedCampus={selectedCampus}
                      setSelectedCampus={handleCampusSelect}
                      onNext={() => goTo(1)}
                      campusLoading={campusLoading}
                    />
                  )}
                  {step === 1 && (
                    <Step2
                      documents={documents}
                      jobs={jobs}
                      isDocumentCompleted={isDocumentCompleted}
                      getUploadedDocumentByType={getUploadedDocumentByType}
                      deletingDocType={deletingDocType}
                      canDeleteUploadedDocument={canDeleteUploadedDocument}
                      removeJob={removeJob}
                      setActiveTab={setActiveTab}
                      setForceShowVerification={setForceShowVerification}
                      openUploadOverlay={openUploadOverlay}
                      setDeleteModalState={setDeleteModalState}
                      englishProficiencyDetails={
                        resolvedEnglishProficiencyDetails
                      }
                      noEnglishTest={noEnglishTest}
                      handleCheckboxChange={handleCheckboxChange}
                      agreeToTerms={agreeToTerms}
                      setAgreeToTerms={setAgreeToTerms}
                      setErrorMsg={setErrorMsg}
                      errorMsg={errorMsg}
                      onBack={() => goTo(0)}
                      onSubmit={handleSubmitApplication}
                      submitting={submitting}
                    />
                  )}
                  {step === 2 && (
                    <Step3
                      coursesCards={coursesCards}
                      selectedCampus={selectedCampus}
                      selectedIntakeMonth={selectedIntakeMonthFormatted}
                      onClose={closePopupAndReset}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        )}
      </motion.div>

      {/* OCR Document upload and verification modals */}
      {submitting && (
        <div className="fixed inset-0 bg-white/60 backdrop-blur-[2px] flex flex-col items-center justify-center z-[2000] rounded-2xl">
          <Loader size={48} className="text-[#3777FF] animate-spin mb-4" />
          <p className="text-[#3777FF] font-bold text-xl ff_poppins animate-pulse">
            Submitting your application...
          </p>
        </div>
      )}
      {showVerifyEmail && (
        <div className="fixed left-0 top-0 z-[999] flex h-full w-full items-center justify-center">
          <span
            onClick={() => setShowVerifyEmail(false)}
            className="absolute h-full w-full bg-black/40 z-[99]"
          ></span>
          <EmailVerification setEmailVerificationPopup={setShowVerifyEmail} />
        </div>
      )}
      {uploadOverlayOpen &&
        !documents.find((d) => d.id === currentDocumentId)?.disabled && (
          <DocumentUploadOverlay
            key={currentDocumentId || "document-upload-overlay"}
            isOpen={uploadOverlayOpen}
            handleClose={closeUploadOverlay}
            onClose={closeUploadOverlay}
            documentName={getCurrentDocumentName()}
            studentId={user?.uid}
            onFileAccepted={handleDocumentFileAccepted}
          />
        )}

      {shouldShowVerifyModal && (
        <div className="fixed inset-0 bg-black/50 z-[150] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-3xl rounded-2xl shadow-xl overflow-hidden relative flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center p-6 border-b bg-gray-50">
              <h2 className="text-xl font-semibold text-gray-800 ff_poppins">
                Verify Extracted Details
              </h2>
              <button
                onClick={() => setForceShowVerification(false)}
                className="text-gray-500 hover:text-gray-700 font-bold px-2"
              >
                X
              </button>
            </div>

            <div className="flex flex-col flex-1 overflow-hidden">
              <div className="flex gap-2 overflow-x-auto border-b px-6 pt-4 bg-gray-50/50">
                {reviewJobs.map((job) => (
                  <button
                    key={job.docId}
                    onClick={() => setActiveTab(job.docId)}
                    className={`px-4 py-3 border-b-2 font-medium text-sm whitespace-nowrap outline-none transition-colors ${
                      activeTab === job.docId
                        ? "border-blue-600 text-blue-800 bg-white rounded-t-lg"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    {job.documentName}
                  </button>
                ))}
              </div>

              <div className="flex-1 overflow-y-auto w-full p-2">
                {activeReviewJob?.extractedData && (
                  <ConfirmExtractedDetails
                    inline
                    documentName={activeReviewJob.documentName}
                    extractedData={activeReviewJob.extractedData}
                    editErrors={activeReviewJob.editErrors || {}}
                    isLoading={activeReviewJob.status === "saving"}
                    onChange={(field, value) =>
                      updateExtractedData(activeTab, field, value)
                    }
                    onConfirm={async () => {
                      const completedDocId = activeTab;
                      if (!completedDocId) return;
                      const isCompleted =
                        await submitVerification(completedDocId);
                      if (isCompleted) {
                        markDocumentAsUploaded(completedDocId);
                        removeJob(completedDocId);
                      }
                    }}
                    onClose={() => removeJob(activeTab)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showConfirmation}
        hasUploadedDocs={documents.some((doc) => isDocumentCompleted(doc) || !!jobs[doc.id])}
        onConfirm={() => {
          setShowConfirmation(false);
        }}
        onCancel={() => {
          closePopupAndReset();
        }}
      />

      <DeleteDocumentModal
        isOpen={deleteModalState.isOpen}
        documentName={deleteModalState.docName}
        isDeleting={
          !!deleteModalState.docType &&
          deletingDocType === deleteModalState.docType
        }
        onCancel={() =>
          setDeleteModalState({ isOpen: false, docType: "", docName: "" })
        }
        onConfirm={() => handleDeleteDocument(deleteModalState.docType)}
      />

      {showMismatchAlert && mismatchDetails.length > 0 && (
        <div className="fixed inset-0 bg-black/50 z-[150] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-[500px] rounded-2xl shadow-xl overflow-hidden relative p-8">
            <div className="flex flex-col items-center text-center">
              <div className="bg-blue-100 p-3 rounded-full mb-4">
                <Info className="text-blue-600 w-8 h-8" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2 ff_poppins">
                Your Details Don&apos;t Match
              </h2>
              <p className="text-gray-600 mb-4 ff_poppins text-sm">
                The course search details you used are different from
                what&apos;s saved in your profile. This can change your
                eligibility outcome.
              </p>
              <ul className="text-sm text-gray-600 ff_poppins list-disc list-inside mb-6 text-left w-full">
                {mismatchDetails.map((diff, idx) => (
                  <li key={idx} className="mb-1">
                    {diff}
                  </li>
                ))}
              </ul>
              <div className="flex flex-col w-full gap-3">
                <button
                  onClick={handleUseProfileDetails}
                  className="w-full py-3 sm:py-4 px-6 font-semibold ff_poppins text-white text-sm md:text-base rounded-xl gradient_button_shadow bg-button-gradient text-center hover:scale-105 duration-300 ease-in-out"
                >
                  Refresh With Profile Details
                </button>
                <button
                  onClick={() => {
                    setMismatchAccepted(true);
                    setShowMismatchAlert(false);
                  }}
                  className="w-full py-3 sm:py-4 px-6 font-semibold ff_poppins text-gray-600 text-sm md:text-base rounded-xl border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
                >
                  Continue With Current Details
                </button>
              </div>
            </div>
            <button
              onClick={() => setShowMismatchAlert(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <XCircle size={24} />
            </button>
          </div>
        </div>
      )}

      {showEligibilityWarning && (
        <div className="fixed inset-0 bg-black/50 z-[150] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-[500px] rounded-2xl shadow-xl overflow-hidden relative p-8">
            <div className="flex flex-col items-center text-center">
              <div className="bg-red-100 p-3 rounded-full mb-4">
                <AlertTriangle className="text-red-600 w-8 h-8" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2 ff_poppins">
                Prerequisites Not Met
              </h2>
              <div className="text-gray-600 mb-6 ff_poppins text-left">
                {eligibilityResult?.reasons &&
                eligibilityResult.reasons.length > 0 ? (
                  <ul className="list-disc pl-4 space-y-1 text-sm">
                    {eligibilityResult.reasons.map((reason, idx) => (
                      <li key={idx}>{reason}</li>
                    ))}
                  </ul>
                ) : (
                  <p>
                    Based on your current details, you do not meet the
                    prerequisites for this course.
                  </p>
                )}
              </div>
              <div className="flex flex-col w-full gap-3">
                <button
                  onClick={() => {
                    setShowEligibilityWarning(false);
                    closePopupAndReset();
                    router.push("/dashboard");
                  }}
                  className="w-full py-4 px-6 bg-[#3777FF] text-white rounded-xl font-semibold ff_poppins shadow-lg hover:bg-blue-600 transition-colors"
                >
                  Update My Details
                </button>
                <button
                  onClick={() => {
                    setShowEligibilityWarning(false);
                    closePopupAndReset();
                  }}
                  className="w-full py-4 px-6 border border-gray-200 text-gray-600 rounded-xl font-semibold ff_poppins hover:bg-gray-50 transition-colors"
                >
                  Go Back
                </button>
              </div>
            </div>
            <button
              onClick={() => setShowEligibilityWarning(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <XCircle size={24} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
